import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Per-device column layout — ordered list of visible columns and an
 * unordered set of hidden ones. Used by the reports tab's column picker
 * to support reorder + add/remove + save-across-reloads.
 *
 * The stored payload shape is `{ visible: string[], hidden: string[] }`.
 * A previous, simpler version of this hook (`useColumnVisibility`) wrote
 * a plain `string[]` of hidden ids under the key
 * `reports:workOrders:columnVisibility:v1`. To avoid interpreting that
 * payload as ordered visibles, callers should use a distinct key
 * (…:columnLayout:v2) for the new shape.
 */

export interface ColumnLayoutStore {
	getItem(key: string): string | null;
	setItem(key: string, value: string): void;
	removeItem(key: string): void;
}

const memoryStore = new Map<string, string>();
const inMemoryStore: ColumnLayoutStore = {
	getItem: (k) => memoryStore.get(k) ?? null,
	setItem: (k, v) => { memoryStore.set(k, v); },
	removeItem: (k) => { memoryStore.delete(k); },
};

export function getDefaultColumnLayoutStore(): ColumnLayoutStore {
	if (typeof globalThis !== 'undefined'
		&& typeof (globalThis as { localStorage?: unknown }).localStorage === 'object'
		&& (globalThis as { localStorage: Storage }).localStorage !== null) {
		const ls = (globalThis as { localStorage: Storage }).localStorage;
		return {
			getItem: (k) => ls.getItem(k),
			setItem: (k, v) => ls.setItem(k, v),
			removeItem: (k) => ls.removeItem(k),
		};
	}
	return inMemoryStore;
}

export interface ColumnLayout {
	/** Visible columns in display order. */
	visible: string[];
	/** Hidden columns (order doesn't matter, but we keep a stable one). */
	hidden: string[];
}

/**
 * Reconciles a persisted layout against the current set of known columns:
 *   - Unknown ids are dropped (so a renamed/removed column can't get stuck).
 *   - Brand-new ids not present in the persisted payload land in `visible`
 *     unless flagged in `defaultHiddenIds`, in which case they go to `hidden`.
 *   - If there's no persisted payload at all, ids flagged in `defaultHiddenIds`
 *     are placed in `hidden`; the rest go to `visible` in the order provided.
 *
 * Pure function — exported so tests can exercise the tolerance rules
 * without mounting the hook.
 */
export function reconcileLayout(
	raw: string | null,
	allIds: readonly string[],
	defaultHiddenIds: readonly string[] = [],
): ColumnLayout {
	const allowed = new Set(allIds);
	const hiddenByDefault = new Set(defaultHiddenIds);
	const defaultLayout: ColumnLayout = {
		visible: allIds.filter((id) => !hiddenByDefault.has(id)),
		hidden: allIds.filter((id) => hiddenByDefault.has(id)),
	};

	if (!raw) return defaultLayout;

	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return defaultLayout;
	}
	if (!parsed || typeof parsed !== 'object') return defaultLayout;

	const p = parsed as { visible?: unknown; hidden?: unknown };
	const rawVisible = Array.isArray(p.visible) ? p.visible : [];
	const rawHidden = Array.isArray(p.hidden) ? p.hidden : [];

	const seen = new Set<string>();
	const visible: string[] = [];
	for (const id of rawVisible) {
		if (typeof id === 'string' && allowed.has(id) && !seen.has(id)) {
			visible.push(id);
			seen.add(id);
		}
	}
	const hidden: string[] = [];
	for (const id of rawHidden) {
		if (typeof id === 'string' && allowed.has(id) && !seen.has(id)) {
			hidden.push(id);
			seen.add(id);
		}
	}
	// Brand-new ids — those not in the persisted payload at all — land in
	// `hidden` if they're marked default-hidden, otherwise `visible` so the
	// column shows up automatically.
	for (const id of allIds) {
		if (seen.has(id)) continue;
		if (hiddenByDefault.has(id)) hidden.push(id);
		else visible.push(id);
		seen.add(id);
	}
	return { visible, hidden };
}

/**
 * Pure reorder helper: moves `fromId` to land at the position currently
 * occupied by `toId`. If the drag dropped on a non-member, the array is
 * returned unchanged. Exported so the picker's drag-drop handler can
 * reuse the same semantics the up/down arrows produce, and so tests
 * can lock down the "move-towards" semantics (drop above vs below the
 * hovered item).
 */
export function reorderVisibleIds(
	visible: readonly string[],
	fromId: string,
	toId: string,
): string[] {
	const from = visible.indexOf(fromId);
	const to = visible.indexOf(toId);
	if (from < 0 || to < 0 || from === to) return [...visible];
	const next = [...visible];
	const [moved] = next.splice(from, 1);
	// After the splice, `to` is still the correct destination index when
	// moving DOWNWARD (to - 1 would over-correct) and the correct target
	// when moving UPWARD — `splice(to, 0, ...)` places `moved` at `to`
	// pushing the current occupant forward. Result: drop-on-X lands the
	// dragged item in X's slot.
	next.splice(to, 0, moved);
	return next;
}

/**
 * Drag an id across two lists. `sourceList` and `destList` are treated as
 * disjoint; if the id isn't in `sourceList`, both lists are returned
 * unchanged. If `beforeId` is in `destList` the id lands at that index
 * (pushing `beforeId` forward); otherwise the id is appended to the end
 * of `destList`. Mirrors reorderVisibleIds's drop-on-target semantics so
 * the picker's in-list and cross-list drops feel consistent.
 */
export function moveIdAcrossLists(
	sourceList: readonly string[],
	destList: readonly string[],
	id: string,
	beforeId?: string,
): { sourceList: string[]; destList: string[] } {
	const srcIdx = sourceList.indexOf(id);
	if (srcIdx < 0) {
		return { sourceList: [...sourceList], destList: [...destList] };
	}
	const nextSource = [...sourceList];
	nextSource.splice(srcIdx, 1);

	const nextDest = [...destList];
	// Avoid duplicating if the id is somehow in both lists.
	const existing = nextDest.indexOf(id);
	if (existing >= 0) nextDest.splice(existing, 1);

	const insertAt = beforeId ? nextDest.indexOf(beforeId) : -1;
	if (insertAt >= 0) {
		nextDest.splice(insertAt, 0, id);
	} else {
		nextDest.push(id);
	}
	return { sourceList: nextSource, destList: nextDest };
}

export interface UseColumnLayoutOptions {
	storageKey: string;
	/** All known column ids (deterministic order — used for defaults + pruning). */
	allIds: readonly string[];
	/**
	 * Column ids that start hidden when there's no persisted layout. Once the
	 * user customizes their layout, these ids behave like any other column.
	 */
	defaultHiddenIds?: readonly string[];
	/** Optional store override for tests. */
	store?: ColumnLayoutStore;
}

export interface UseColumnLayoutReturn {
	layout: ColumnLayout;
	/** Replace the layout wholesale (used by the picker's Apply button). */
	setLayout: (next: ColumnLayout) => void;
	/** Reset to the default (all visible, original order). */
	reset: () => void;
}

export function useColumnLayout(options: UseColumnLayoutOptions): UseColumnLayoutReturn {
	const { storageKey, allIds, defaultHiddenIds } = options;
	const store = options.store ?? getDefaultColumnLayoutStore();
	const allIdsKey = allIds.join('|');
	const defaultHiddenKey = (defaultHiddenIds ?? []).join('|');

	const [layout, setLayoutState] = useState<ColumnLayout>(
		() => reconcileLayout(store.getItem(storageKey), allIds, defaultHiddenIds),
	);

	// Re-reconcile if the column set changes (e.g. HMR adds a column).
	useEffect(() => {
		setLayoutState(reconcileLayout(store.getItem(storageKey), allIds, defaultHiddenIds));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storageKey, allIdsKey, defaultHiddenKey]);

	const setLayout = useCallback((next: ColumnLayout) => {
		const reconciled = reconcileLayout(JSON.stringify(next), allIds, defaultHiddenIds);
		setLayoutState(reconciled);
		// Only persist if the layout actually deviates from default; keeps
		// storage tidy and "factory defaults" behaviour after a Reset.
		const defaultVisible = allIds.filter((id) => !(defaultHiddenIds ?? []).includes(id));
		const isDefault = reconciled.visible.length === defaultVisible.length
			&& reconciled.visible.every((id, i) => id === defaultVisible[i]);
		if (isDefault) {
			store.removeItem(storageKey);
		} else {
			store.setItem(storageKey, JSON.stringify(reconciled));
		}
	}, [store, storageKey, allIds, defaultHiddenIds]);

	const reset = useCallback(() => {
		const hidden = allIds.filter((id) => (defaultHiddenIds ?? []).includes(id));
		const visible = allIds.filter((id) => !(defaultHiddenIds ?? []).includes(id));
		setLayoutState({ visible, hidden });
		store.removeItem(storageKey);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [store, storageKey, allIdsKey, defaultHiddenKey]);

	return useMemo(() => ({ layout, setLayout, reset }), [layout, setLayout, reset]);
}
